import { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, User2, GalleryHorizontal, Mail, FileText, Loader2, NotebookPen,
  Copy, ExternalLink, Check, ChevronDown, ChevronRight, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import * as Tabs from '@radix-ui/react-tabs';
import axios from 'axios';
import {
  StatusBadge,
  RatingBadge,
  ActionButton,
  SectionHeader,
  CurrentStateLine,
} from '../components/VisualAtoms';
import {
  humanize,
  RATING_BUTTON_VARIANTS,
  toneForRating,
  TONE,
} from '../theme/adminVisualSystem';
import ArtistWorksGrid from '../components/ArtistWorksGrid';
import ArtistIntakePanel from '../components/ArtistIntakePanel';
import ArtistWebsiteBlock from '../components/ArtistWebsiteBlock';
import ArtistPFPUploader from '../components/ArtistPFPUploader';
import { Field, GalleryInput, GalleryTextarea } from '@/components/forms/GalleryFormPrimitives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS = ['average', 'good', 'very_good', 'genius'];

function ratingNumeric(key) {
  // DB column is text — keep this only as a legacy compat shim.
  return { average: 'average', good: 'good', very_good: 'very_good', genius: 'genius' }[key] || null;
}
function ratingKey(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  // Backward compat: older rows may still have ints 1..4
  return { 1: 'average', 2: 'good', 3: 'very_good', 4: 'genius' }[v] || null;
}

const PROFILE_INPUTS = [
  ['artist_name', 'ARTIST NAME'],
  ['email', 'EMAIL'],
  ['location', 'LOCATION'],
  ['city', 'CITY'],
  ['country', 'COUNTRY'],
  ['main_medium_style', 'MEDIUM / STYLE'],
  ['main_social_media', 'SOCIAL'],
  ['portfolio_link', 'PORTFOLIO'],
  ['website', 'WEBSITE'],
  ['instagram', 'INSTAGRAM'],
];

export default function ArtistDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { exhibitionId } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');

  const [profileEdit, setProfileEdit] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const reload = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/artists/${id}`)
      .then((r) => {
        setData(r.data);
        setProfileEdit(buildEdit(r.data.profile));
      })
      .catch(() => toast.error('Not found'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const updateField = (k, v) => setProfileEdit((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const body = { ...profileEdit };
      // strip empty strings that should remain unchanged on the server side
      const res = await axios.patch(`${API}/admin/artists/${id}`, body);
      setData((d) => ({ ...d, profile: res.data }));
      setProfileEdit(buildEdit(res.data));
      toast.success('Profile saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const setRating = async (key) => {
    setSavingProfile(true);
    try {
      const numeric = ratingNumeric(key);
      const res = await axios.patch(`${API}/admin/artists/${id}`, { global_rating: numeric });
      setData((d) => ({ ...d, profile: res.data }));
      setProfileEdit((p) => ({ ...p, global_rating: res.data.global_rating }));
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading || !data) {
    return <p className="text-[#444] font-mono text-sm p-8">Loading...</p>;
  }
  const { profile, applications = [], email_logs = [] } = data;

  return (
    <div className="space-y-6 max-w-5xl">
      <button
        onClick={() => navigate('/admin/artists')}
        className="flex items-center gap-2 text-xs font-mono text-[#666] hover:text-white transition-colors"
        style={{ cursor: 'pointer' }}
        data-testid="back-to-artists"
      >
        <ArrowLeft className="w-4 h-4" /> Back to artists
      </button>

      {/* Header card */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="border border-[#1a1a1a] bg-[#0d0d0d] p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Avatar */}
          <ArtistPFPUploader
            artistId={id}
            currentUrl={profile.pfp_url}
            onUpdated={(updated) => setData((d) => ({ ...d, profile: { ...d.profile, pfp_url: updated.pfp_url, pfp_storage_path: updated.pfp_storage_path, pfp_bucket_name: updated.pfp_bucket_name } }))}
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">ARTIST</p>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white tracking-tight mt-1 break-words">
              {profile.artist_name || 'Unnamed'}
            </h1>
            <p className="text-sm font-mono text-[#aaa] mt-1 break-all">{profile.email}</p>
            <div className="flex flex-wrap gap-2 mt-3 items-center">
              <RatingBadge rating={ratingKey(profile.global_rating)} strong />
              {profile.source && (
                <span className="text-[10px] font-mono px-2 py-0.5 border border-[#333] text-[#aaa] uppercase tracking-[0.12em]">
                  {humanize(profile.source)}
                </span>
              )}
              {profile.submission_status && (
                <span className="text-[10px] font-mono px-2 py-0.5 border border-[#333] text-[#aaa] uppercase tracking-[0.12em]">
                  {humanize(profile.submission_status)}
                </span>
              )}
              {profile.asset_submission_status && profile.asset_submission_status !== 'not_sent' && (
                <span
                  className="text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.12em]"
                  style={{
                    color: TONE.cyan.text,
                    borderColor: TONE.cyan.border,
                    background: TONE.cyan.bg,
                  }}
                >
                  Assets · {humanize(profile.asset_submission_status)}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <p className="text-[10px] font-mono text-[#666] tracking-[0.18em] uppercase">
              Artist intake
            </p>
            <p className="text-[11px] font-mono text-[#888] max-w-[200px] text-right leading-snug">
              Send or copy the form link in the panel below.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <Tabs.Root value={tab} onValueChange={setTab} data-testid="artist-tabs">
        <Tabs.List className="flex flex-wrap gap-2 border-b border-[#1a1a1a] pb-3">
          <TabTrigger value="profile" icon={GalleryHorizontal} count={null} testId="tab-profile">Profile</TabTrigger>
          <TabTrigger value="details" icon={User2} count={null} testId="tab-details">Details</TabTrigger>
          <TabTrigger value="applications" icon={FileText} count={applications.length} testId="tab-applications">Applications</TabTrigger>
          <TabTrigger value="emails" icon={Mail} count={email_logs.length} testId="tab-emails">Emails</TabTrigger>
        </Tabs.List>

        {/* Profile tab — fast curator overview + portfolio */}
        <Tabs.Content value="profile" className="mt-6 space-y-6">
          <ProfileQuickActions
            artistId={id}
            exhibitionId={exhibitionId || profile.source_exhibition_id}
            artist={profile}
          />

          <ProfileRatingBlock
            currentRating={profile.global_rating}
            disabled={savingProfile}
            onChange={setRating}
          />

          <CuratorNoteCollapsible
            note={profile.internal_note}
            onJumpToDetails={() => setTab('details')}
          />

          <ArtistWebsiteBlock artistId={id} />

          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="space-y-3"
            data-testid="profile-works-section"
          >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-outfit text-xl sm:text-2xl font-semibold text-white tracking-tight">
                  Artist Works
                </h3>
                <p className="text-[11px] font-mono text-[#888] mt-1 tracking-[0.05em]">
                  Files submitted by this artist
                </p>
              </div>
            </div>
            <ArtistWorksGrid artistId={id} compact />
          </motion.section>
        </Tabs.Content>

        {/* Details tab — admin editing + intake */}
        <Tabs.Content value="details" className="mt-6 space-y-6">
          <ArtistIntakePanel
            artistId={id}
            exhibitionId={exhibitionId || profile.source_exhibition_id}
            openCallApplicationId={profile.source_open_call_application_id || undefined}
            assetForm={{
              sent_at: profile.asset_form_sent_at,
              opened_at: profile.asset_form_opened_at,
              submitted_at: profile.asset_form_submitted_at,
            }}
            defaultFormType={(profile.source === 'open_call' || profile.source_open_call_application_id) ? 'short' : 'long'}
            onLinkGenerated={() => reload()}
          />

          <motion.section
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
            data-testid="profile-edit-section"
          >
            <SectionHeader icon={User2} title="Profile Details" helper="Edit artist information, internal notes, and contact links." />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROFILE_INPUTS.map(([k, label]) => (
                <Field key={k} label={label}>
                  <GalleryInput
                    type="text"
                    value={profileEdit[k] || ''}
                    onChange={(e) => updateField(k, e.target.value)}
                    data-testid={`profile-${k}`}
                  />
                </Field>
              ))}
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5">
              <Field label="ARTIST BIO">
                <GalleryTextarea
                  rows={3}
                  value={profileEdit.artist_bio || ''}
                  onChange={(e) => updateField('artist_bio', e.target.value)}
                  data-testid="profile-artist_bio"
                />
              </Field>
              <Field label="ARTIST STATEMENT">
                <GalleryTextarea
                  rows={4}
                  value={profileEdit.artist_statement || ''}
                  onChange={(e) => updateField('artist_statement', e.target.value)}
                  data-testid="profile-artist_statement"
                />
              </Field>
              <Field label="INTERNAL NOTE (curator only)">
                <GalleryTextarea
                  rows={3}
                  value={profileEdit.internal_note || ''}
                  onChange={(e) => updateField('internal_note', e.target.value)}
                  data-testid="profile-internal_note"
                />
              </Field>
            </div>
            <div className="mt-6">
              <ActionButton
                variant="primary"
                icon={savingProfile ? Loader2 : NotebookPen}
                onClick={saveProfile}
                disabled={savingProfile}
                data-testid="save-profile-btn"
              >
                {savingProfile ? 'Saving…' : 'Save Profile'}
              </ActionButton>
            </div>
          </motion.section>

          <section className="border border-[#1a1a1a] bg-[#0d0d0d] p-6" data-testid="profile-rating-section">
            <SectionHeader title="Curator Rating" helper="Long-term value of this artist (across all exhibitions)." />
            <div className="flex flex-wrap gap-1.5">
              {RATINGS.map((r) => (
                <ActionButton
                  key={r}
                  variant={RATING_BUTTON_VARIANTS[r]}
                  active={ratingKey(profile.global_rating) === r}
                  disabled={savingProfile}
                  onClick={() => setRating(r)}
                  dense
                  data-testid={`profile-rating-${r}`}
                >
                  {humanize(r)}
                </ActionButton>
              ))}
            </div>
            <CurrentStateLine
              label="Current rating"
              value={ratingKey(profile.global_rating)}
              tone={toneForRating(ratingKey(profile.global_rating))}
              emptyLabel="Unrated"
            />
          </section>
        </Tabs.Content>

        {/* Applications tab */}
        <Tabs.Content value="applications" className="mt-6">
          {applications.length === 0 ? (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
              <p className="text-[#666] font-mono text-sm">No applications linked.</p>
            </div>
          ) : (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] divide-y divide-[#111]">
              {applications.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3 text-xs font-mono">
                  <div className="min-w-0">
                    <p className="text-white truncate">{a.artist_name} — {humanize(a.main_medium_style) || '—'}</p>
                    <p className="text-[10px] text-[#666] mt-0.5">
                      Submitted · {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={a.status} />
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/open-call/${a.id}`)}
                      data-clickable="true"
                      className="text-[10px] font-mono px-3 py-1.5 border border-[#222] text-[#90E0EF] hover:bg-[#90E0EF]/10 hover:border-[#90E0EF]/40 uppercase tracking-[0.14em] transition-colors"
                    >
                      Open
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tabs.Content>

        {/* Emails tab */}
        <Tabs.Content value="emails" className="mt-6">
          {email_logs.length === 0 ? (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
              <p className="text-[#666] font-mono text-sm">No emails sent yet.</p>
            </div>
          ) : (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] divide-y divide-[#111]">
              {email_logs.map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between gap-3 text-xs font-mono">
                  <div className="min-w-0">
                    <p className="text-white truncate">{l.subject}</p>
                    <p className="text-[10px] text-[#666] mt-0.5">
                      {l.template_key || '—'} · {l.created_at ? new Date(l.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <span className={l.status === 'sent' ? 'text-[#7ed4a0]' : 'text-red-400'}>
                    {l.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

function buildEdit(profile) {
  if (!profile) return {};
  const out = {};
  for (const [k] of PROFILE_INPUTS) out[k] = profile[k] || '';
  out.artist_bio = profile.artist_bio || '';
  out.artist_statement = profile.artist_statement || '';
  out.internal_note = profile.internal_note || '';
  out.global_rating = profile.global_rating || null;
  return out;
}

function TabTrigger({ value, icon: Icon, children, count, testId }) {
  return (
    <Tabs.Trigger
      value={value}
      data-testid={testId}
      data-clickable="true"
      className={
        "group inline-flex items-center gap-2 px-3 py-2 border rounded-none text-[11px] font-mono uppercase tracking-[0.14em] transition-all " +
        "border-[#222] text-[#777] hover:text-white hover:border-[#333] " +
        "data-[state=active]:border-[#90E0EF] data-[state=active]:text-[#90E0EF] data-[state=active]:bg-[#90E0EF]/5 data-[state=active]:shadow-[0_0_10px_rgba(144,224,239,0.18)]"
      }
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
      {count != null && (
        <span className="text-[9px] font-mono text-[#555] ml-1">({count})</span>
      )}
    </Tabs.Trigger>
  );
}


// ─── Profile-tab sub-components ─────────────────────────────────

function ProfileQuickActions({ artistId, exhibitionId, artist }) {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  const social = artist?.main_social_media?.trim() || artist?.instagram?.trim() || '';
  const email = artist?.email?.trim() || '';
  const website = artist?.website?.trim() || artist?.portfolio_link?.trim() || '';

  const normalizeUrl = (raw) => {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('@')) return `https://instagram.com/${raw.slice(1)}`;
    return `https://${raw}`;
  };

  const copyLongLink = async () => {
    if (!exhibitionId) {
      toast.error('Select an exhibition first');
      return;
    }
    setCopying(true);
    try {
      const res = await axios.post(`${API}/admin/artists/${artistId}/intake-link`, {
        exhibition_id: exhibitionId,
        send_email: false,
        mode: 'full',
        frontend_origin: window.location.origin,
      });
      try {
        await navigator.clipboard.writeText(res.data.link);
        toast.success('Long form link copied.');
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      } catch {
        toast.message('Link generated. Open Details tab to copy manually.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate link.');
    } finally {
      setCopying(false);
    }
  };

  const openSocial = () => social && window.open(normalizeUrl(social), '_blank', 'noopener');
  const openEmail = () => email && window.open(`mailto:${email}`, '_self');
  const openWebsite = () => website && window.open(normalizeUrl(website), '_blank', 'noopener');

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-4 sm:p-5 flex flex-wrap items-center gap-2"
      data-testid="profile-quick-actions"
    >
      <ActionButton
        variant="primary"
        icon={copying ? Loader2 : (copied ? Check : Copy)}
        onClick={copyLongLink}
        disabled={copying}
        data-testid="copy-long-form-link-btn"
      >
        {copying ? 'Generating…' : (copied ? 'Copied' : 'Copy Long Form Link')}
      </ActionButton>
      <ActionButton
        variant="ghost"
        icon={ExternalLink}
        onClick={openSocial}
        disabled={!social}
        data-testid="quick-social-btn"
      >
        Social
      </ActionButton>
      <ActionButton
        variant="ghost"
        icon={Mail}
        onClick={openEmail}
        disabled={!email}
        data-testid="quick-email-btn"
      >
        Email
      </ActionButton>
      <ActionButton
        variant="ghost"
        icon={ExternalLink}
        onClick={openWebsite}
        disabled={!website}
        data-testid="quick-website-btn"
      >
        Website
      </ActionButton>
    </motion.div>
  );
}

function ProfileRatingBlock({ currentRating, disabled, onChange }) {
  const cur = ratingKey(currentRating);
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.04 }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] p-4 sm:p-5"
      data-testid="profile-rating-quick"
    >
      <SectionHeader title="Curator Rating" helper="Grade the long-term value of this artist." />
      <div className="flex flex-wrap gap-1.5">
        {RATINGS.map((r) => (
          <ActionButton
            key={r}
            variant={RATING_BUTTON_VARIANTS[r]}
            active={cur === r}
            disabled={disabled}
            onClick={() => onChange(r)}
            dense
            data-testid={`profile-quick-rating-${r}`}
          >
            {humanize(r)}
          </ActionButton>
        ))}
      </div>
      <CurrentStateLine
        label="Current"
        value={cur}
        tone={toneForRating(cur)}
        emptyLabel="Unrated"
      />
    </motion.section>
  );
}

function CuratorNoteCollapsible({ note, onJumpToDetails }) {
  const [open, setOpen] = useState(false);
  const hasNote = !!(note && note.trim());
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.06 }}
      className="border border-[#1a1a1a] bg-[#0d0d0d] overflow-hidden"
      data-testid="curator-note-collapsible"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-clickable="true"
        data-testid="curator-note-toggle"
        className="w-full px-4 sm:px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#111] transition-colors"
        style={{ cursor: 'pointer' }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${hasNote ? 'bg-[#90E0EF]' : 'bg-[#333]'}`} />
          <MessageSquare className="w-3.5 h-3.5 text-[#666]" />
          <p className="text-[11px] font-mono text-white tracking-[0.18em] uppercase">
            Curator Note
          </p>
          <span className="text-[10px] font-mono text-[#666] truncate">
            {hasNote ? '· Internal note available' : '· No note yet'}
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#666] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#666] shrink-0" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            className="border-t border-[#1a1a1a]"
            data-testid="curator-note-content"
          >
            <div className="px-4 sm:px-5 py-4 space-y-3">
              {hasNote ? (
                <p className="text-sm font-mono text-[#cfcfcf] leading-relaxed whitespace-pre-wrap">
                  {note}
                </p>
              ) : (
                <p className="text-xs font-mono text-[#666]">No internal note has been written yet.</p>
              )}
              <button
                type="button"
                onClick={onJumpToDetails}
                data-clickable="true"
                data-testid="curator-note-edit-jump"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono text-[#90E0EF] hover:text-white tracking-[0.14em] uppercase transition-colors"
                style={{ cursor: 'pointer' }}
              >
                Edit in Details <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

